import { searchResultService, userService } from '@/services';
import {
  CatalogProducts,
  AiCatalogSearchItem,
  SearchResultReq,
  DocSearchResultReq,
  CatergorySearchReq,
  SearchRetestReq,
  SearchCatalogSupplementalData,
  SearchResultRes,
} from '@/types/search-result';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAppStore } from '@/store/app-store';

const mapAiCatalogSearchItem = (item: AiCatalogSearchItem): CatalogProducts => ({
  source: 'AI',
  rootCatalogNumber: item.rootCatalogNumber,
  childCatalogNumber: item.catalogNumber,
  webCatalogNumber: item.webCatalogNumber,
  catalogName: item.catalogName,
  brand: item.brand,
  imageUrl: item.imageUrl,
  synonym: null,
  cas: item.casNumber,
  formula: item.formula,
  hazardClass: null,
  einecs: null,
  packagingGroup: null,
  mdl: item.mdl,
  refNumber: '',
  brandImage: null,
  hasCollided: false,
});

const getSupplementalCatalogResultDTOs = (response: { data: SearchCatalogSupplementalData } | null) => {
  if (!response) {
    return [];
  }

  const payload = response.data;
  if (payload && typeof payload === 'object' && 'items' in payload) {
    return Array.isArray(payload.items) ? payload.items.map(mapAiCatalogSearchItem) : [];
  }

  if (payload && typeof payload === 'object' && 'catalogResultDTOs' in payload) {
    return payload.catalogResultDTOs ?? [];
  }

  return [];
};

const createEmptySearchResult = (keywordSearchError = false): SearchResultRes => ({
  refinements: [],
  catalogResultDTOs: [],
  count: 0,
  dbcList: [],
  decList: [],
  redirect: false,
  pdpRedirectSku: null,
  pdpRedirectType: null,
  pageByFrontend: false,
  keywordSearchError,
});

const getCatalogRootKey = (product: CatalogProducts) => product.rootCatalogNumber || product.childCatalogNumber;

const getUniqueCatalogResults = (catalogResultDTOs: CatalogProducts[]) => {
  const seenRootKeys = new Set<string>();

  return catalogResultDTOs.filter((product) => {
    const rootKey = getCatalogRootKey(product);
    if (seenRootKeys.has(rootKey)) {
      return false;
    }
    seenRootKeys.add(rootKey);
    return true;
  });
};

const getNonDuplicateSupplementalCatalogResults = (
  existingCatalogResultDTOs: CatalogProducts[],
  supplementalCatalogResultDTOs: CatalogProducts[]
) => {
  const existingRootKeys = new Set(existingCatalogResultDTOs.map(getCatalogRootKey));

  return getUniqueCatalogResults(supplementalCatalogResultDTOs).filter(
    (product) => !existingRootKeys.has(getCatalogRootKey(product))
  );
};

const mergeSupplementalSearchResults = (
  searchResultData: SearchResultRes,
  aiCatalogResultDTOs: CatalogProducts[],
  npiCatalogResultDTOs: CatalogProducts[]
): SearchResultRes => {
  const keywordCatalogResultDTOs = searchResultData.catalogResultDTOs ?? [];
  const uniqueAiCatalogResultDTOs = getUniqueCatalogResults(aiCatalogResultDTOs);
  const aiRootKeys = new Set(uniqueAiCatalogResultDTOs.map(getCatalogRootKey));
  const filteredKeywordCatalogResultDTOs = keywordCatalogResultDTOs.filter(
    (product) => !aiRootKeys.has(getCatalogRootKey(product))
  );
  const removedKeywordCount = keywordCatalogResultDTOs.length - filteredKeywordCatalogResultDTOs.length;
  const nonDuplicateNpiCatalogResultDTOs = getNonDuplicateSupplementalCatalogResults(
    [...uniqueAiCatalogResultDTOs, ...filteredKeywordCatalogResultDTOs],
    npiCatalogResultDTOs
  );

  return {
    ...searchResultData,
    count:
      (searchResultData.count ?? 0) -
      removedKeywordCount +
      uniqueAiCatalogResultDTOs.length +
      nonDuplicateNpiCatalogResultDTOs.length,
    catalogResultDTOs: [
      ...uniqueAiCatalogResultDTOs,
      ...nonDuplicateNpiCatalogResultDTOs,
      ...filteredKeywordCatalogResultDTOs,
    ],
  };
};

export const useQuerySearchResult = (searchResultReq: SearchResultReq) => {
  const { isAnonymous } = useAppStore();
  const searchQuery = searchResultReq.query || '';
  const isFirstPage = searchResultReq.pageNo === 1;
  const supplementalEnabled = !!searchResultReq.countryCode && isFirstPage;
  const aiSearchAccessEnabled = !isAnonymous && searchResultReq.countryCode === 'cn' && isFirstPage;
  const aiSearchAccessQuery = useQuery({
    queryKey: ['AI_SEARCH_ACCESS'],
    queryFn: async () => {
      try {
        const res = await userService.getAiSearchAccess();
        return res.code === '200' && !!res.data?.enabled;
      } catch {
        return false;
      }
    },
    enabled: aiSearchAccessEnabled,
    staleTime: Infinity,
  });
  const aiSearchEnabled = aiSearchAccessEnabled && aiSearchAccessQuery.isSuccess && !!aiSearchAccessQuery.data;
  const searchResultQuery = useQuery({
    queryKey: ['SEARCH_RESULT', searchResultReq],
    queryFn: async () => {
      try {
        const searchResultResponse = await searchResultService.getSearchResult(searchResultReq);
        return searchResultResponse.data ?? createEmptySearchResult();
      } catch {
        return createEmptySearchResult(true);
      }
    },
    enabled: !!searchResultReq.countryCode,
    staleTime: 0,
    cacheTime: 0,
  });

  const aiSearchResultQuery = useQuery({
    queryKey: ['SEARCH_RESULT_AI', searchResultReq.countryCode, searchQuery],
    queryFn: async () => {
      try {
        const response = await searchResultService.getSearchCatalogAi({
          query: searchQuery,
        });
        return getSupplementalCatalogResultDTOs(response);
      } catch {
        return [];
      }
    },
    enabled: aiSearchEnabled,
    staleTime: 0,
    cacheTime: 0,
  });

  const npiSearchResultQuery = useQuery({
    queryKey: ['SEARCH_RESULT_NPI', searchResultReq.countryCode, searchQuery],
    queryFn: async () => {
      try {
        const response = await searchResultService.getSearchKeywordNpi({
          query: searchQuery,
        });
        return getSupplementalCatalogResultDTOs(response);
      } catch {
        return [];
      }
    },
    enabled: supplementalEnabled,
    staleTime: 0,
    cacheTime: 0,
  });

  const data = useMemo(() => {
    const searchResultData = searchResultQuery.data;
    const aiCatalogResultDTOs = aiSearchResultQuery.data ?? [];
    const npiCatalogResultDTOs = npiSearchResultQuery.data ?? [];

    if (!searchResultData || !isFirstPage || (!aiCatalogResultDTOs.length && !npiCatalogResultDTOs.length)) {
      return searchResultData;
    }

    return mergeSupplementalSearchResults(searchResultData, aiCatalogResultDTOs, npiCatalogResultDTOs);
  }, [aiSearchResultQuery.data, isFirstPage, npiSearchResultQuery.data, searchResultQuery.data]);
  const hasSupplementalResults = useMemo(() => {
    const searchResultData = searchResultQuery.data;
    const aiCatalogResultDTOs = aiSearchResultQuery.data ?? [];
    const npiCatalogResultDTOs = npiSearchResultQuery.data ?? [];

    if (!searchResultData || !isFirstPage || (!aiCatalogResultDTOs.length && !npiCatalogResultDTOs.length)) {
      return false;
    }

    const uniqueAiCatalogResultDTOs = getUniqueCatalogResults(aiCatalogResultDTOs);
    const aiRootKeys = new Set(uniqueAiCatalogResultDTOs.map(getCatalogRootKey));
    const filteredKeywordCatalogResultDTOs = (searchResultData.catalogResultDTOs ?? []).filter(
      (product) => !aiRootKeys.has(getCatalogRootKey(product))
    );
    const nonDuplicateNpiCatalogResultDTOs = getNonDuplicateSupplementalCatalogResults(
      [...uniqueAiCatalogResultDTOs, ...filteredKeywordCatalogResultDTOs],
      npiCatalogResultDTOs
    );

    return uniqueAiCatalogResultDTOs.length > 0 || nonDuplicateNpiCatalogResultDTOs.length > 0;
  }, [aiSearchResultQuery.data, isFirstPage, npiSearchResultQuery.data, searchResultQuery.data]);
  const isAiSearchLoading = aiSearchEnabled && aiSearchResultQuery.isLoading;

  return {
    ...searchResultQuery,
    data,
    hasSupplementalResults,
    isAiSearchLoading,
    isSupplementalLoading: isAiSearchLoading || (supplementalEnabled && npiSearchResultQuery.isLoading),
  };
};

export const useQueryCatalogNotes = (catalogNumbers: string[] = [], enabled = true) => {
  return useQuery({
    queryKey: ['CATALOG_NOTES', catalogNumbers],
    queryFn: async () => {
      const res = await searchResultService.getCatalogNotes(catalogNumbers);
      return res.data;
    },
    enabled: enabled && catalogNumbers?.length > 0,
  });
};

export const useQueryDocSearchResult = (searchResultReq: DocSearchResultReq) => {
  const { page, pageSize, documentType, catalogNumber, resetPage } = searchResultReq;
  return useQuery({
    queryKey: ['DOC_SEARCH_RESULT', page, pageSize, documentType, catalogNumber],
    queryFn: async () => {
      const res = await searchResultService.getDocSearchResult(searchResultReq);
      return res;
    },
    enabled: catalogNumber !== undefined && !resetPage,
    keepPreviousData: true,
  });
};
export const useQueryCategorySearchResult = (searchResultReq: CatergorySearchReq) => {
  return useQuery({
    queryKey: ['CAT_SEARCH_RESULT', searchResultReq],
    queryFn: async () => {
      const res = await searchResultService.getCategorySearch(searchResultReq);
      return res;
    },
    staleTime: 0,
    cacheTime: 0,
    enabled: !!searchResultReq.countryCode,
  });
};

export const useQueryChildCatalogs = (catalogNumber: string, enabled = true) => {
  return useQuery({
    queryKey: ['QUERY_CHILD_CATALOGS', catalogNumber],
    queryFn: async () => {
      const res = await searchResultService.getChildCatalogs(catalogNumber);
      return res.filter((item) => item.skuStatus === 'RELEASED') || [];
    },
    enabled: !!catalogNumber && enabled,
  });
};

export const useQueryCatalogInfo = () => {
  return useMutation({
    mutationFn: (catalogNumbers: string[]) => searchResultService.getCatalogInfo(catalogNumbers),
  });
};
export const useMutationSearchResult = () => {
  return useMutation({
    mutationFn: (searchResultReq: SearchRetestReq) => searchResultService.getRetestSearchResult(searchResultReq),
  });
};
